import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { getStatusSummary } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { Sheet, Input } from "@/pages/Familia";
import { PendientesAsignar } from "@/components/PendientesAsignar";
import { formatApiErrorDetail } from "@/lib/api";

const MiniCounter = ({ color, value, label }) => (
  <div className="flex-1 text-center">
    <p className="font-heading text-xl font-extrabold" style={{ color }}>{value}</p>
    <p className="text-xs font-semibold text-[#6E7A74]">{label}</p>
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]"
    >
      {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
    </select>
  </div>
);

const EMPTY = {
  alias: "", direccion: "", tipo_tenencia: "propiedad", titulares: "", referencia_catastral: "",
  tipo_inmueble: "piso", uso: "vivienda habitual", municipio: "", provincia: "", codigo_postal: "",
  fecha_adquisicion: "", aseguradora_hogar: "", numero_poliza: "", notas: "", icon: "Home",
};

export default function Viviendas() {
  const navigate = useNavigate();
  const { viviendas, getMattersByVivienda, createVivienda } = useData();
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!modal.alias.trim()) return toast.error("Indica un alias para la vivienda.");
    if (!modal.direccion.trim()) return toast.error("Indica la dirección de la vivienda.");
    setBusy(true);
    try {
      await createVivienda({ ...modal, alias: modal.alias.trim() });
      toast.success("Vivienda añadida");
      setModal(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <button data-testid="viviendas-back" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]">
        <Icons.ChevronLeft size={22} /> Volver
      </button>

      <header className="aldia-fade-up flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
          <Icons.Home size={26} className="text-[#3A6B56]" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Viviendas</h1>
          <p className="text-base text-[#6E7A74]">Tus casas y todo lo asociado: suministros, seguros, IBI y más.</p>
        </div>
      </header>

      <button data-testid="vivienda-add" onClick={() => setModal({ ...EMPTY })} className="w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2">
        <Icons.Plus size={24} /> Añadir vivienda
      </button>

      {viviendas.length === 0 ? (
        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 text-center" data-testid="viviendas-empty">
          <Icons.Home size={28} className="text-[#9AA39D] mx-auto mb-2" />
          <p className="text-base text-[#6E7A74]">Aún no has añadido ninguna vivienda. Crea la primera para agrupar sus suministros y contratos.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="viviendas-list">
          {viviendas.map((viv, idx) => {
            const s = getStatusSummary(getMattersByVivienda(viv.id));
            return (
              <button key={viv.id} data-testid={`vivienda-card-${viv.id}`} onClick={() => navigate(`/vivienda/${viv.id}`)}
                className="aldia-fade-up w-full text-left bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-[#3A6B56]/50 transition-all"
                style={{ animationDelay: `${idx * 70}ms` }}>
                <div className="flex items-center gap-4">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center">
                    <Icons.Home size={30} className="text-[#3A6B56]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl font-bold text-[#1C2421]">{viv.alias}</h3>
                    <p className="text-sm text-[#6E7A74] truncate">{viv.direccion || "Sin dirección"}</p>
                    {(viv.municipio || viv.tipo_tenencia) && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1 mt-1.5">
                        <Icons.MapPin size={13} /> {[viv.municipio, viv.tipo_tenencia].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                  <Icons.ChevronRight size={22} className="text-[#C4BDB0] shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#E5DFD5]/70">
                  <MiniCounter color="#DC2626" value={s.attention} label="Requiere atención" />
                  <div className="w-px h-8 bg-[#E5DFD5]" />
                  <MiniCounter color="#D97706" value={s.upcoming} label="Próximamente" />
                  <div className="w-px h-8 bg-[#E5DFD5]" />
                  <MiniCounter color="#16A34A" value={s.controlled} label="Documentación al día" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <PendientesAsignar />

      <div className="flex items-start gap-2 bg-[#F6F3EE] rounded-xl p-4">
        <Icons.Info size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
        <p className="text-sm text-[#6E7A74] leading-relaxed">
          Luz, gas, agua, internet, seguro de hogar, IBI y comunidad se agrupan en la vivienda a la que pertenecen. Al subir un documento con dirección, AL DÍA propondrá vincularlo a la vivienda correcta.
        </p>
      </div>

      {modal && (
        <Sheet onClose={() => setModal(null)} testid="vivienda-form">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">Añadir vivienda</h2>
          <Input label="Alias" value={modal.alias} onChange={(v) => setModal({ ...modal, alias: v })} testid="vivienda-alias" placeholder="Ej. Casa Las Rozas" />
          <Input label="Dirección completa" value={modal.direccion} onChange={(v) => setModal({ ...modal, direccion: v })} testid="vivienda-direccion" placeholder="Calle, número, piso…" />
          <Input label="Municipio" value={modal.municipio} onChange={(v) => setModal({ ...modal, municipio: v })} testid="vivienda-municipio" placeholder="Opcional" />
          <Input label="Provincia" value={modal.provincia} onChange={(v) => setModal({ ...modal, provincia: v })} testid="vivienda-provincia" placeholder="Opcional" />
          <Input label="Código postal" value={modal.codigo_postal} onChange={(v) => setModal({ ...modal, codigo_postal: v })} testid="vivienda-cp" placeholder="Opcional" />
          <Select label="Tipo de tenencia" value={modal.tipo_tenencia} onChange={(v) => setModal({ ...modal, tipo_tenencia: v })} testid="vivienda-tenencia" options={["propiedad", "alquiler", "otro"]} />
          <Select label="Tipo de inmueble" value={modal.tipo_inmueble} onChange={(v) => setModal({ ...modal, tipo_inmueble: v })} testid="vivienda-tipo" options={["piso", "chalet", "apartamento", "local", "otro"]} />
          <Select label="Uso" value={modal.uso} onChange={(v) => setModal({ ...modal, uso: v })} testid="vivienda-uso" options={["vivienda habitual", "segunda residencia", "alquiler", "otro"]} />
          <Input label="Titular(es)" value={modal.titulares} onChange={(v) => setModal({ ...modal, titulares: v })} testid="vivienda-titulares" placeholder="Opcional" />
          <Input label="Referencia catastral" value={modal.referencia_catastral} onChange={(v) => setModal({ ...modal, referencia_catastral: v })} testid="vivienda-catastral" placeholder="Opcional" />
          <Input label="Aseguradora del hogar" value={modal.aseguradora_hogar} onChange={(v) => setModal({ ...modal, aseguradora_hogar: v })} testid="vivienda-aseguradora" placeholder="Opcional" />
          <Input label="Nº de póliza" value={modal.numero_poliza} onChange={(v) => setModal({ ...modal, numero_poliza: v })} testid="vivienda-poliza" placeholder="Opcional" />
          <Input label="Notas" value={modal.notas} onChange={(v) => setModal({ ...modal, notas: v })} testid="vivienda-notas" placeholder="Opcional" />
          <button data-testid="vivienda-save" onClick={save} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60">{busy ? "Guardando…" : "Guardar vivienda"}</button>
        </Sheet>
      )}
    </div>
  );
}
